import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Search, ArrowBack } from "@mui/icons-material";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSystemHealthOverview } from "../../redux/actions";
import dayjs from "dayjs";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export default function ResponseTimesByEndpoint() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [durationFilter, setDurationFilter] = useState("Last 24 Hours");
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    dispatch(getSystemHealthOverview());
  }, [dispatch]);

  const [sortDirection, setSortDirection] = useState("asc");

  const responseTimeList =
    useSelector(
      (state) =>
        state.dashboard.getSystemHealthDashboard.data?.details?.response_time
          ?.data
    ) || [];

  let filteredData = responseTimeList.filter((row) => {
    const rowTime = dayjs(row.LastChecked, "DD/MM/YYYY, hh:mm A");
    const diffInHours = dayjs().diff(rowTime, "hour");

    if (durationFilter === "Last 1 Hour") return diffInHours < 1;
    if (durationFilter === "Last 6 Hours") return diffInHours < 6;
    if (durationFilter === "Last 12 Hours") return diffInHours < 12;
    return true;
  });

  if (statusFilter !== "All") {
    filteredData = filteredData.filter((row) => row.Status === statusFilter);
  }

  if (searchQuery.trim() !== "") {
    filteredData = filteredData.filter((row) =>
      row.APIEndpoint.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  filteredData = filteredData.sort((a, b) => {
    const dateA = dayjs(a.LastChecked, "DD/MM/YYYY, hh:mm A");
    const dateB = dayjs(b.LastChecked, "DD/MM/YYYY, hh:mm A");
    return sortDirection === "asc"
      ? dateA.isBefore(dateB)
        ? -1
        : 1
      : dateA.isAfter(dateB)
      ? -1
      : 1;
  });

  return (
    <DashboardLayout>
      <Container maxWidth={false} sx={{ py: 4, px: 5 }}>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl
              size="small"
              sx={{
                width: "220px",
                backgroundColor: "#fff",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#BDBDBD",
                  },
                  "&:hover fieldset": {
                    borderColor: "#7B2D43",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#BDBDBD",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#757575",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#757575",
                },
              }}
            >
              <InputLabel id="duration-label">Duration</InputLabel>
              <Select
                labelId="duration-label"
                label="Duration"
                value={durationFilter}
                onChange={(e) => setDurationFilter(e.target.value)}
              >
                <MenuItem value="Last 1 Hour">Last 1 Hour</MenuItem>
                <MenuItem value="Last 6 Hours">Last 6 Hours</MenuItem>
                <MenuItem value="Last 12 Hours">Last 12 Hours</MenuItem>
                <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
              </Select>
            </FormControl>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                backgroundColor: "#fff",
                borderRadius: "3px",
                width: "220px",
                height: "40px",
                "& .MuiSelect-select": {
                  padding: "10px 14px",
                  color: statusFilter !== "All" ? "#000" : "#888",
                },
              }}
              renderValue={(selected) =>
                selected !== "All" ? selected : "Status"
              }
            >
              <MenuItem value="All">Status</MenuItem>
              <MenuItem value="Healthy">Healthy</MenuItem>
              <MenuItem value="Slight Delay">Slight Delay</MenuItem>
              <MenuItem value="Slow">Slow</MenuItem>
              <MenuItem value="Critical">Critical</MenuItem>
            </Select>

            <TextField
              placeholder="Search"
              size="small"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                width: "300px",
                height: "42px",
                "& .MuiOutlinedInput-input": {
                  padding: "10px 14px",
                },
              }}
              InputProps={{
                startAdornment: <Search sx={{ color: "#888", mr: 1 }} />,
              }}
            />
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              borderColor: "#7B2D43",
              color: "#7B2D43",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#7B2D43",
                backgroundColor: "transparent",
              },
            }}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        </Box>

        <Typography
          variant="h6"
          fontWeight={600}
          mb={6}
          sx={{ textAlign: "left" }}
        >
          Average Response Times by Endpoint
        </Typography>

        <Box display="flex" justifyContent="center">
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #ddd",
              width: "100%",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#D0C5CA" }}>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    API End point
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Average Response time
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Last Checked
                      </Typography>
                      <Box display="flex" flexDirection="column" sx={{ ml: 1 }}>
                        <Box
                          onClick={() => setSortDirection("asc")}
                          sx={{
                            background: "rgb(225, 225, 225)",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                            mb: "2px",
                          }}
                        >
                          <ArrowDropUpIcon
                            sx={{
                              fontSize: 16,
                              color:
                                sortDirection === "asc" ? "#7B2D43" : "#999",
                            }}
                          />
                        </Box>
                        <Box
                          onClick={() => setSortDirection("desc")}
                          sx={{
                            background: "rgb(225, 225, 225)",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        >
                          <ArrowDropDownIcon
                            sx={{
                              fontSize: 16,
                              color:
                                sortDirection === "desc" ? "#7B2D43" : "#999",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        "& td": {
                          py: 2.5,
                          borderBottom: "1px solid #ddd",
                        },
                      }}
                    >
                      <TableCell>{row.APIEndpoint}</TableCell>
                      <TableCell>
                        {Math.round(row.AvgResponseTime)} ms
                      </TableCell>
                      <TableCell
                        sx={{
                          color:
                            row.Status === "Healthy"
                              ? "green"
                              : row.Status === "Slight Delay"
                              ? "orange"
                              : "red",
                          fontWeight: 500,
                        }}
                      >
                        {row.Status}
                      </TableCell>
                      <TableCell>{row.LastChecked}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Container>
    </DashboardLayout>
  );
}
